"use client";

import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-28 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center px-4 py-2 text-[15px] font-default text-foreground hover:text-foreground hover:bg-transparent focus:bg-transparent transition-colors">
                <span>{item.label}</span>
                <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 bottom-1.5 h-1 w-1 rounded-lg bg-secondary opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6">
        <div className="h-10 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-44 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-36 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onNavigate} data-index={i} className="font-default text-3xl text-foreground py-3 border-b border-border hover:text-secondary transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const phoneNumber = "(555) 467-3829";
  const phoneHref = "tel:+15554673829";
  return <section data-section-id="919" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? "bg-background/90 backdrop-blur-md shadow-md border-b border-border" : "bg-background border-b border-border"}`}>
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-16">
        <div className="flex items-center">
          <Link to="/" aria-label="Hope Torchlighters home" className="flex items-center">
            <WvcLogo className="h-9 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="33" className="header-menu">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden lg:flex items-center gap-6">
          <Link to={phoneHref} className="flex items-center gap-2 text-primary hover:text-secondary transition-colors">
            <Phone className="h-4 w-4" />
            <span className="font-default text-sm tabular-nums tracking-tight">
              {phoneNumber}
            </span>
          </Link>
          <Button asChild className="rounded-lg bg-secondary text-secondary-foreground hover:bg-primary px-6 h-11 font-default text-sm shadow-md">
            <Link to="/ConnectPage">Get Involved</Link>
          </Button>
        </div>

        <div className="lg:hidden flex items-center">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-accent rounded-lg">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-0 flex flex-col">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>

              <div className="flex items-center justify-between h-20 px-6 border-b border-border">
                <Link to="/" onClick={() => setMobileOpen(false)} aria-label="Hope Torchlighters home">
                  <WvcLogo className="h-9 w-auto" />
                </Link>
              </div>

              <div className="flex-1 overflow-y-auto py-8">
                <MenuProvider menu_id="33">
                  <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                </MenuProvider>
              </div>

              <div className="px-6 pb-8 pt-4 border-t border-border space-y-4">
                <Link to={phoneHref} onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 text-primary py-2">
                  <Phone className="h-5 w-5" />
                  <span className="font-default text-lg tabular-nums">
                    {phoneNumber}
                  </span>
                </Link>
                <Button asChild className="w-full rounded-lg bg-secondary text-secondary-foreground hover:bg-primary h-12 font-default text-base shadow-md">
                  <Link to="/ConnectPage" onClick={() => setMobileOpen(false)}>
                    Get Involved
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}